import {
    clampString,
    ClientLogApp,
    ClientLogPayloadV2,
    ClientLogContext,
    ClientLogLevel,
    sanitizeContext,
    sanitizeDeviceMeta,
    sanitizeError,
    sanitizeEventName,
    sanitizeSource,
    sanitizeTags,
    MAX_CORRELATION_ID_LENGTH,
    MAX_MESSAGE_LENGTH,
    MAX_REQUEST_ID_LENGTH,
    MAX_ROUTE_LENGTH,
    MAX_USER_AGENT_LENGTH,
} from './client-log-contract'
import {redactCapabilityPath} from './capability-path'
import {
    resolveBrowserFamily,
    resolveDeviceType,
    resolveOsFamily,
} from './device-detection'

const APP: ClientLogApp = 'client'
const LOG_ENDPOINT = '/api/logs/client'
const CORRELATION_COOKIE = 'fitro_correlation_id'
const DEDUPE_WINDOW_MS = 30_000
const MAX_DIAGNOSTIC_TEXT_LENGTH = 1000
const MAX_DIAGNOSTIC_STACK_LENGTH = 5000
const FACEBOOK_IAB_NAVIGATION_SOURCE = 'iabjs://navigation_performance_logger_android'
const FACEBOOK_IAB_NAVIGATION_ERROR_MESSAGE = 'Error invoking postMessage: Java object is gone'

const WARN_FORWARD_ALLOWLIST = new Set<string>([
    'navigation_state_load_failed',
    'navigation_state_save_failed',
    'navigation_tab_not_found',
    'i18n_load_failed',
    'i18n_change_locale_failed',
    'i18n_invalid_locale',
    'i18n_translation_error',
    'i18n_unsupported_locale',
    'command_palette_recent_load_failed',
    'command_palette_recent_save_failed',
    'clerk_env_warning',
    'ui_heartbeat',
    'ui_main_thread_stall',
    'ui_render_timing',
    'ui_resumed_after_suspension',
])

const recentLogs = new Map<string, number>()

type ExceptionDiagnostics = {
    error_type: string
    error_kind: string | null
    error_name: string | null
    error_code: string | null
    error_status: number | null
    error_has_stack: boolean
    error_message: string | null
    error_message_length: number | null
    error_stack: string | null
    error_stack_frame_count: number | null
    error_stack_top: string | null
    error_stack_frame_1: string | null
    error_stack_frame_2: string | null
    error_stack_function: string | null
    error_stack_file: string | null
    error_stack_line: number | null
    error_stack_column: number | null
    error_cause_type: string | null
    error_cause_kind: string | null
    error_cause_name: string | null
    error_cause_code: string | null
    error_cause_status: number | null
    error_cause_message: string | null
    error_cause_message_length: number | null
}

export type ClientLogArgs = {
    event: string
    source: string
    message: string
    context?: Record<string, unknown>
    tags?: string[]
    error?: unknown
}

const isDebugEnv = process.env.NEXT_PUBLIC_APP_ENV === 'dev' || process.env.NODE_ENV === 'development'

const readCookie = (name: string): string | null => {
    if (typeof document === 'undefined') return null

    const prefix = `${encodeURIComponent(name)}=`
    const parts = document.cookie.split(';')
    for (const part of parts) {
        const trimmed = part.trim()
        if (!trimmed.startsWith(prefix)) continue

        const value = trimmed.slice(prefix.length)
        return value ? decodeURIComponent(value) : null
    }

    return null
}

const resolveCorrelationId = (): string | undefined => {
    return clampString(readCookie(CORRELATION_COOKIE) ?? undefined, MAX_CORRELATION_ID_LENGTH)
}

const resolveRequestId = (): string => {
    const generated = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `rid_${Date.now()}`

    return clampString(generated, MAX_REQUEST_ID_LENGTH) ?? generated
}

const resolveRoute = (): string | undefined => {
    if (typeof window === 'undefined') return undefined
    return clampString(redactCapabilityPath(window.location.pathname), MAX_ROUTE_LENGTH)
}

const resolveUserAgent = (): string | undefined => {
    if (typeof navigator === 'undefined') return undefined
    return clampString(navigator.userAgent, MAX_USER_AGENT_LENGTH)
}

const DISPLAY_MODE_QUERIES = [
    'fullscreen',
    'standalone',
    'minimal-ui',
    'window-controls-overlay',
    'browser',
] as const

const resolveDisplayModeMeta = (): Pick<NonNullable<ClientLogPayloadV2['device']>, 'displayMode' | 'standalone'> => {
    const iosStandalone = (navigator as Navigator & {standalone?: unknown}).standalone === true

    if (typeof window.matchMedia === 'function') {
        for (const displayMode of DISPLAY_MODE_QUERIES) {
            try {
                if (window.matchMedia(`(display-mode: ${displayMode})`).matches) {
                    return {
                        displayMode,
                        standalone: iosStandalone || displayMode !== 'browser',
                    }
                }
            } catch {
                // Keep telemetry best-effort; display-mode support varies by browser.
            }
        }
    }

    return {
        displayMode: iosStandalone ? 'standalone' : 'unknown',
        standalone: iosStandalone,
    }
}

const resolveDeviceMeta = (): ClientLogPayloadV2['device'] => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return undefined
    }

    const userAgent = navigator.userAgent ?? ''
    const touchPointsRaw = navigator.maxTouchPoints
    const touchPoints = typeof touchPointsRaw === 'number' && Number.isFinite(touchPointsRaw)
        ? Math.max(0, Math.trunc(touchPointsRaw))
        : undefined

    let timezone: string | undefined
    try {
        timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
    } catch {
        timezone = undefined
    }

    const viewport = Number.isFinite(window.innerWidth) && Number.isFinite(window.innerHeight)
        ? `${Math.max(0, Math.trunc(window.innerWidth))}x${Math.max(0, Math.trunc(window.innerHeight))}`
        : undefined

    return sanitizeDeviceMeta({
        deviceType: resolveDeviceType(userAgent, touchPoints),
        osFamily: resolveOsFamily(userAgent, touchPoints),
        browserFamily: resolveBrowserFamily(userAgent),
        language: navigator.language,
        timezone,
        viewport,
        touchPoints,
        ...resolveDisplayModeMeta(),
    })
}

const resolveErrorType = (error: unknown): string => {
    if (error === null) return 'null'
    if (error === undefined) return 'undefined'
    if (error instanceof Error) return 'error'
    if (Array.isArray(error)) return 'array'
    return typeof error
}

const resolveErrorKind = (error: unknown): string | null => {
    if (error instanceof Error) {
        const ctorName = error.constructor?.name
        if (typeof ctorName === 'string' && ctorName.trim().length > 0) return ctorName
        return error.name || 'Error'
    }
    if (error && typeof error === 'object') {
        const ctorName = (error as {constructor?: {name?: unknown}}).constructor?.name
        if (typeof ctorName === 'string' && ctorName.trim().length > 0) return ctorName
        return 'Object'
    }
    return null
}

const resolveErrorCode = (error: unknown): string | null => {
    if (!error || typeof error !== 'object') return null
    const code = (error as {code?: unknown}).code
    if (typeof code === 'string' && code.trim().length > 0) return code
    if (typeof code === 'number' && Number.isFinite(code)) return String(code)
    return null
}

const resolveErrorStatus = (error: unknown): number | null => {
    if (!error || typeof error !== 'object') return null
    const status = (error as {status?: unknown}).status
    if (typeof status === 'number' && Number.isFinite(status)) return Math.trunc(status)
    return null
}

const resolveErrorMessage = (error: unknown): string | null => {
    if (error instanceof Error) {
        return typeof error.message === 'string' && error.message.length > 0 ? error.message : null
    }
    if (typeof error === 'string') return error
    if (error && typeof error === 'object') {
        const message = (error as {message?: unknown}).message
        if (typeof message === 'string' && message.length > 0) return message
    }
    return null
}

const resolveErrorCause = (error: unknown): unknown => {
    if (!error || typeof error !== 'object' || !('cause' in error)) {
        return null
    }
    return (error as {cause?: unknown}).cause ?? null
}

const redactDiagnosticText = (value: string, maxLength = MAX_DIAGNOSTIC_TEXT_LENGTH): string => {
    const withoutSessionPathValues = value.replace(/(\/sessions\/)[^/?#\s):]+/gi, '$1[redacted]')
    const withoutCredentialQueryValues = withoutSessionPathValues.replace(
        /([?&](?:token|secret|password|authorization|jwt|session|cookie)=)[^&#\s):]+/gi,
        '$1[redacted]'
    )
    const withoutBearerTokens = withoutCredentialQueryValues.replace(
        /\b(bearer\s+)[a-z0-9._~+/=-]{16,}/gi,
        '$1[redacted]'
    )
    const withoutKeyValueCredentials = withoutBearerTokens.replace(
        /\b(token|secret|password|authorization|jwt|session|cookie)=([^\s,;&):]+)/gi,
        '$1=[redacted]'
    )

    return clampString(withoutKeyValueCredentials, maxLength) ?? withoutKeyValueCredentials
}

export const sanitizeClientDiagnosticText = (value: string): string => redactDiagnosticText(value)

type ParsedStackFrame = {
    raw: string
    functionName: string | null
    file: string | null
    line: number | null
    column: number | null
}

const normalizeStackSource = (value: string): string | null => {
    const trimmed = value.trim()
    if (!trimmed) return null

    const withoutWrapping = trimmed.replace(/^\(/, '').replace(/\)$/, '')
    const withoutHashOrQuery = withoutWrapping.split('#')[0]?.split('?')[0] ?? withoutWrapping

    try {
        const currentOrigin = typeof window !== 'undefined' ? window.location.origin : undefined
        const url = currentOrigin ? new URL(withoutHashOrQuery, currentOrigin) : new URL(withoutHashOrQuery)
        if (url.protocol === 'http:' || url.protocol === 'https:') {
            const normalized = currentOrigin && url.origin === currentOrigin
                ? url.pathname
                : `${url.host}${url.pathname}`
            return clampString(normalized, 256) ?? normalized
        }
    } catch {
        // fall through to raw normalized source
    }

    return clampString(withoutHashOrQuery, 256) ?? withoutHashOrQuery
}

const parseInteger = (value: string): number | null => {
    const parsed = Number.parseInt(value, 10)
    return Number.isFinite(parsed) ? parsed : null
}

const parseStackLocation = (
    value: string
): {file: string | null; line: number | null; column: number | null} | null => {
    const trimmed = value.trim()
    if (!trimmed) return null

    const lastColon = trimmed.lastIndexOf(':')
    if (lastColon <= 0) {
        return {file: normalizeStackSource(trimmed), line: null, column: null}
    }

    const secondColon = trimmed.lastIndexOf(':', lastColon - 1)
    if (secondColon <= 0) {
        return {file: normalizeStackSource(trimmed), line: null, column: null}
    }

    const line = parseInteger(trimmed.slice(secondColon + 1, lastColon))
    const column = parseInteger(trimmed.slice(lastColon + 1))
    if (line === null || column === null) {
        return {file: normalizeStackSource(trimmed), line: null, column: null}
    }

    return {
        file: normalizeStackSource(trimmed.slice(0, secondColon)),
        line,
        column,
    }
}

const parseStackFrame = (value: string): ParsedStackFrame | null => {
    const trimmed = value.trim()
    if (!trimmed) return null

    let functionName: string | null = null
    let location: string

    if (trimmed.startsWith('at ')) {
        const body = trimmed.slice(3).trim()
        const parenIndex = body.lastIndexOf(' (')
        if (parenIndex >= 0 && body.endsWith(')')) {
            functionName = clampString(body.slice(0, parenIndex).trim() || undefined, 256) ?? null
            location = body.slice(parenIndex + 2, -1)
        } else {
            location = body
        }
    } else if (/^[^\s@]+@/.test(trimmed)) {
        const atIndex = trimmed.indexOf('@')
        functionName = clampString(trimmed.slice(0, atIndex).trim() || undefined, 256) ?? null
        location = trimmed.slice(atIndex + 1)
    } else {
        return null
    }

    const parsedLocation = parseStackLocation(location)
    const normalizedLocation = parsedLocation?.file
        ? `${parsedLocation.file}${parsedLocation.line !== null ? `:${parsedLocation.line}` : ''}${parsedLocation.column !== null ? `:${parsedLocation.column}` : ''}`
        : clampString(location.trim(), 256) ?? location.trim()
    const summary = functionName
        ? `at ${functionName} (${normalizedLocation})`
        : `at ${normalizedLocation}`

    return {
        raw: clampString(summary, 256) ?? summary,
        functionName,
        file: parsedLocation?.file ?? null,
        line: parsedLocation?.line ?? null,
        column: parsedLocation?.column ?? null,
    }
}

const extractParsedStackFrames = (error: unknown): ParsedStackFrame[] => {
    if (!(error instanceof Error) || typeof error.stack !== 'string' || error.stack.length === 0) {
        return []
    }

    return error.stack
        .split('\n')
        .map(parseStackFrame)
        .filter((frame): frame is ParsedStackFrame => Boolean(frame))
}

const buildExceptionDiagnostics = (error: unknown): ExceptionDiagnostics => {
    const message = resolveErrorMessage(error)
    const stackFrames = extractParsedStackFrames(error)
    const stack = error instanceof Error && typeof error.stack === 'string' && error.stack.length > 0
        ? redactDiagnosticText(error.stack, MAX_DIAGNOSTIC_STACK_LENGTH)
        : null
    const topFrame = stackFrames[0] ?? null
    const cause = resolveErrorCause(error)
    const causeMessage = resolveErrorMessage(cause)

    return {
        error_type: resolveErrorType(error),
        error_kind: resolveErrorKind(error),
        error_name: error instanceof Error ? (error.name || 'Error') : resolveErrorKind(error),
        error_code: resolveErrorCode(error),
        error_status: resolveErrorStatus(error),
        error_has_stack: error instanceof Error && typeof error.stack === 'string' && error.stack.length > 0,
        error_message: typeof message === 'string' ? redactDiagnosticText(message) : null,
        error_message_length: typeof message === 'string' ? message.length : null,
        error_stack: stack,
        error_stack_frame_count: stackFrames.length > 0 ? stackFrames.length : null,
        error_stack_top: topFrame?.raw ?? null,
        error_stack_frame_1: stackFrames[1]?.raw ?? null,
        error_stack_frame_2: stackFrames[2]?.raw ?? null,
        error_stack_function: topFrame?.functionName ?? null,
        error_stack_file: topFrame?.file ?? null,
        error_stack_line: topFrame?.line ?? null,
        error_stack_column: topFrame?.column ?? null,
        error_cause_type: cause === null ? null : resolveErrorType(cause),
        error_cause_kind: resolveErrorKind(cause),
        error_cause_name: cause instanceof Error ? (cause.name || 'Error') : resolveErrorKind(cause),
        error_cause_code: resolveErrorCode(cause),
        error_cause_status: resolveErrorStatus(cause),
        error_cause_message: typeof causeMessage === 'string' ? redactDiagnosticText(causeMessage) : null,
        error_cause_message_length: typeof causeMessage === 'string' ? causeMessage.length : null,
    }
}

const buildSanitizedError = (diagnostics: ExceptionDiagnostics): {name: string; code?: string; message?: string; stack?: string} => {
    return {
        name: diagnostics.error_name || diagnostics.error_kind || 'Error',
        ...(diagnostics.error_code ? {code: diagnostics.error_code} : {}),
        ...(diagnostics.error_message ? {message: diagnostics.error_message} : {}),
        ...(diagnostics.error_stack ? {stack: diagnostics.error_stack} : diagnostics.error_stack_top ? {stack: diagnostics.error_stack_top} : {}),
    }
}

const buildFallbackExceptionMessage = (diagnostics: ExceptionDiagnostics): string => {
    const name = diagnostics.error_name || diagnostics.error_kind || diagnostics.error_type || 'error'
    const parts = [name]
    if (diagnostics.error_status !== null) parts.push(`status=${diagnostics.error_status}`)
    if (diagnostics.error_code) parts.push(`code=${diagnostics.error_code}`)
    if (diagnostics.error_message) parts.push(`message=${diagnostics.error_message}`)
    return `Unhandled exception (${parts.join(', ')})`
}

const buildPayload = (level: ClientLogLevel, args: ClientLogArgs): ClientLogPayloadV2 => {
    const event = sanitizeEventName(args.event)
    const source = sanitizeSource(args.source)
    const message = clampString(args.message, MAX_MESSAGE_LENGTH) ?? 'unknown_message'

    return {
        schema: 'v2',
        app: APP,
        level,
        event,
        source,
        message,
        route: resolveRoute(),
        userAgent: resolveUserAgent(),
        device: resolveDeviceMeta(),
        correlationId: resolveCorrelationId(),
        requestId: resolveRequestId(),
        tags: sanitizeTags(args.tags),
        context: sanitizeContext(args.context),
        error: sanitizeError(args.error),
        ts: new Date().toISOString(),
    }
}

const appendFingerprintPart = (parts: string[], key: string, value: unknown) => {
    if (typeof value === 'string') {
        const trimmed = value.trim()
        if (trimmed.length > 0) {
            parts.push(`${key}=${trimmed}`)
        }
        return
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        parts.push(`${key}=${Math.trunc(value)}`)
    }
}

const ERROR_DIAGNOSTIC_FINGERPRINT_KEYS = [
    'error_type',
    'error_kind',
    'error_name',
    'error_code',
    'error_status',
    'error_message',
    'error_stack_top',
    'error_stack_function',
    'error_stack_file',
    'error_stack_line',
    'error_stack_column',
    'error_cause_type',
    'error_cause_kind',
    'error_cause_name',
    'error_cause_code',
    'error_cause_status',
    'error_cause_message',
] as const

const GLOBAL_ERROR_FINGERPRINT_KEYS = [
    'filename',
    'line',
    'column',
    'errorName',
    'reasonType',
    'reasonName',
    'reasonCode',
    'reasonPreview',
    'reasonHasStack',
    'hasErrorObject',
] as const

const buildFingerprint = (payload: ClientLogPayloadV2): string => {
    const parts = [
        payload.level,
        payload.event,
        payload.source,
        payload.route ?? '',
    ]

    const context = payload.context as Record<string, unknown> | undefined
    if (context) {
        const diagnosticParts: string[] = []

        for (const key of ERROR_DIAGNOSTIC_FINGERPRINT_KEYS) {
            appendFingerprintPart(diagnosticParts, key, context[key])
        }

        for (const key of GLOBAL_ERROR_FINGERPRINT_KEYS) {
            appendFingerprintPart(diagnosticParts, key, context[key])
        }

        if (diagnosticParts.length > 0) {
            return [...parts, ...diagnosticParts].join('|')
        }
    }

    return [...parts, payload.message].join('|')
}

const isDuplicate = (fingerprint: string): boolean => {
    const now = Date.now()
    const previous = recentLogs.get(fingerprint)

    for (const [key, timestamp] of recentLogs.entries()) {
        if (now - timestamp > DEDUPE_WINDOW_MS) {
            recentLogs.delete(key)
        }
    }

    if (previous && now - previous < DEDUPE_WINDOW_MS) {
        return true
    }

    recentLogs.set(fingerprint, now)
    return false
}

const shouldForward = (payload: ClientLogPayloadV2): boolean => {
    const context = payload.context as Record<string, unknown> | undefined
    if (
        payload.event === 'window_error' &&
        context?.errormessagepreview === FACEBOOK_IAB_NAVIGATION_ERROR_MESSAGE &&
        (payload.source === FACEBOOK_IAB_NAVIGATION_SOURCE || context.filename === FACEBOOK_IAB_NAVIGATION_SOURCE)
    ) {
        return false
    }
    if (payload.level === 'error') return true
    return WARN_FORWARD_ALLOWLIST.has(payload.event)
}

const sendPayload = (payload: ClientLogPayloadV2) => {
    if (typeof window === 'undefined') return

    const body = JSON.stringify(payload)

    try {
        if (typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
            const blob = new Blob([body], {type: 'application/json'})
            navigator.sendBeacon(LOG_ENDPOINT, blob)
            return
        }
    } catch {
        // fallback to fetch below
    }

    fetch(LOG_ENDPOINT, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body,
        keepalive: true,
    }).catch(() => undefined)
}

const emit = (level: ClientLogLevel, args: ClientLogArgs) => {
    if (typeof window === 'undefined') return

    const payload = buildPayload(level, args)
    if (!shouldForward(payload)) return

    const fingerprint = buildFingerprint(payload)
    if (isDuplicate(fingerprint)) return

    sendPayload(payload)
}

export const logClientError = (args: ClientLogArgs) => {
    emit('error', args)
}

export const logClientWarn = (args: ClientLogArgs) => {
    emit('warn', args)
}

export const captureException = (
    error: unknown,
    meta?: Omit<ClientLogArgs, 'error' | 'message'> & {message?: string}
) => {
    const diagnostics = buildExceptionDiagnostics(error)
    const fallbackMessage = buildFallbackExceptionMessage(diagnostics)
    const context = {
        ...diagnostics,
        ...(meta?.context ?? {}),
    }

    logClientError({
        event: meta?.event ?? 'unhandled_exception',
        source: meta?.source ?? 'window',
        message: meta?.message ?? fallbackMessage,
        context,
        tags: meta?.tags,
        error: buildSanitizedError(diagnostics),
    })
}

export const debug = (message: string, context?: ClientLogContext, source = 'client_debug') => {
    if (!isDebugEnv || typeof window === 'undefined') return
    console.debug('[client-debug]', {source, message, context})
}
